import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Button,
  Checkbox,
  Table,
  Tooltip,
  Alert,
  Empty,
  Tag,
  Segmented,
  notification,
} from "antd";
import {
  CloseOutlined,
  DownloadOutlined,
  ReloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  LoadingOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useExportJobs, downloadExportFile } from "./useExportJobs";
import { exportSiteVisitsPdf, PDF_COLUMN_SETS } from "./exportPdf";
import { fetchSiteVisitsForExport } from "./fetchSiteVisitsForExport";
import "./export.scss";

/* One window for the whole export story: what you are about to export at the
   top, everything that has already been exported below it. The old flow put a
   600px "Export Options" modal on top of a 900px file-list modal, which is why
   it had two Export buttons, a Back and a Cancel.

   Two formats share that window, because the choice between them is not a
   different job, it is the same export written differently:

   - Excel is a Hangfire job. The POST only queues work, so nothing here
     blocks: submitting adds a live row, the panel can be closed straight away,
     and useExportJobs keeps watching and raises a notification with a Download
     button when the workbook lands.
   - PDF is built here in the browser, from a single IgnorePagination fetch, and
     saves immediately. There is no server file, so nothing about it appears in
     the "Recent exports" list below — which is why that list is Excel's alone
     and steps aside when PDF is picked. */

const SCOPE_COPY = {
  project: {
    title: "Export site visits",
    subtitle: "Every site visit in this project",
    pill: "Entire project",
    hint: "Filters applied to the table are not included in this export.",
    pdfName: "site-visits",
    pdfTitle: "Site visits",
  },
  user: {
    title: "Export selected site visits",
    subtitle: "Only the cards you ticked in the table",
    pill: "Selection",
    hint: "Only rows selected on the current page are exported.",
    pdfName: "site-visits-selected",
    pdfTitle: "Site visits — selected",
  },
};

const FORMAT_OPTIONS = [
  {
    label: (
      <span className="exportFormatOption">
        <FileExcelOutlined /> Excel
      </span>
    ),
    value: "excel",
  },
  {
    label: (
      <span className="exportFormatOption">
        <FilePdfOutlined /> PDF
      </span>
    ),
    value: "pdf",
  },
];

const COLUMN_SET_OPTIONS = Object.entries(PDF_COLUMN_SETS).map(
  ([value, set]) => ({
    label: set.label,
    value,
  })
);

const formatElapsed = (ms) => {
  const total = Math.max(0, Math.round(ms / 1000));
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  return `${minutes}m ${String(total % 60).padStart(2, "0")}s`;
};

const formatAbsolute = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/* Own relative formatter rather than services/getRelativeTime: that one drops
   the trailing Z before parsing, so a UTC timestamp is read as local time and a
   file created seconds ago reads as hours old. Here the offset is respected and
   a clock ahead of ours degrades to "just now" instead of a negative age. */
const relativeFrom = (value) => {
  if (!value) return "";
  const then = value instanceof Date ? value.getTime() : new Date(value).getTime();
  if (Number.isNaN(then)) return "";

  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (seconds < 45) return "just now";

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return hours === 1 ? "1 hour ago" : `${hours} hours ago`;

  const days = Math.round(hours / 24);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 31) {
    const weeks = Math.round(days / 7);
    return weeks === 1 ? "last week" : `${weeks} weeks ago`;
  }
  const months = Math.round(days / 30);
  if (months < 12) return months === 1 ? "last month" : `${months} months ago`;
  const years = Math.round(days / 365);
  return years === 1 ? "last year" : `${years} years ago`;
};

// Re-render once a second, but only while something is actually counting up.
const useSecondTicker = (active) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [active]);
};

const ExportPanel = ({
  open = false,
  onClose,
  scope = "project",
  selectedSiteVisitIds = [],
}) => {
  const copy = SCOPE_COPY[scope] || SCOPE_COPY.project;
  const isSelection = scope === "user";
  const selectedCount = selectedSiteVisitIds?.length || 0;

  const {
    files,
    pending,
    stalled,
    loadingFiles,
    listError,
    submitting,
    phase,
    lastChecked,
    submit,
    refresh,
    retry,
    dismissStalled,
  } = useExportJobs({ scope, open });

  const [includeMedia, setIncludeMedia] = useState(false);
  const [format, setFormat] = useState("excel");
  const [columnSet, setColumnSet] = useState("key");
  const [buildingPdf, setBuildingPdf] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  const isPdf = format === "pdf";

  useSecondTicker(open && pending.length > 0);

  // A stale error from the last attempt should not outlive the window or greet
  // whoever switches back to Excel and returns.
  useEffect(() => {
    setPdfError(null);
  }, [open, format]);

  const nothingSelected = isSelection && selectedCount === 0;

  const handleGenerate = () => {
    if (nothingSelected || submitting) return;
    submit({
      includeFiles: includeMedia,
      siteVisitIds: isSelection ? selectedSiteVisitIds : undefined,
    });
  };

  const handleDownloadPdf = async () => {
    if (nothingSelected || buildingPdf) return;
    setBuildingPdf(true);
    setPdfError(null);

    try {
      const exportRows = await fetchSiteVisitsForExport({
        siteVisitIds: isSelection ? selectedSiteVisitIds : undefined,
      });

      if (!exportRows.length) {
        setPdfError(
          isSelection
            ? "None of the selected site visits could be loaded. Refresh the table and try again."
            : "This project has no site visits to export yet."
        );
        return;
      }

      const set = PDF_COLUMN_SETS[columnSet] || PDF_COLUMN_SETS.key;
      const result = await exportSiteVisitsPdf({
        rows: exportRows,
        columnSet,
        title: copy.pdfTitle,
        subtitle: `${exportRows.length} site visit${
          exportRows.length === 1 ? "" : "s"
        } · ${isSelection ? "Selection" : "Entire project"} · ${set.label}`,
        fileName: copy.pdfName,
      });

      notification.success({
        key: `export-pdf-${scope}`,
        message: "PDF downloaded",
        description: `${result.count} site visit${
          result.count === 1 ? "" : "s"
        } — ${result.fileName}`,
        duration: 5,
      });
    } catch (error) {
      setPdfError(
        "We couldn't load the site visits for this PDF. Please try again."
      );
    } finally {
      setBuildingPdf(false);
    }
  };

  const rows = useMemo(() => {
    const now = Date.now();
    const live = pending.map((job) => ({
      key: job.id,
      rowType: "pending",
      name: job.includeFiles
        ? "Generating workbook + media"
        : "Generating workbook",
      hint: job.resumed
        ? "Started before this window was opened"
        : job.count
        ? `${job.count} site visit${job.count === 1 ? "" : "s"}`
        : "Whole project",
      when: formatElapsed(now - job.startedAt),
      url: null,
    }));

    const failed = stalled.map((job) => ({
      key: job.id,
      rowType: "stalled",
      name: "Export not confirmed",
      hint: "No file arrived and no job is running",
      when: "—",
      url: null,
    }));

    const ready = files.map((file) => ({
      ...file,
      rowType: "file",
      hint: null,
      when: relativeFrom(file.createdDate),
    }));

    return [...live, ...failed, ...ready];
  }, [pending, stalled, files]);

  const columns = useMemo(
    () => [
      {
        title: "File",
        key: "name",
        render: (_, row) => (
          <div className="exportFileCell">
            <span
              className={`exportFileIcon is-${row.rowType}${
                row.rowType === "pending" && phase !== "watching"
                  ? " is-paused"
                  : ""
              }`}
            >
              {row.rowType === "pending" ? (
                // A spinner next to a "Paused" pill would be a lie.
                phase === "watching" ? (
                  <LoadingOutlined />
                ) : (
                  <ClockCircleOutlined />
                )
              ) : row.rowType === "stalled" ? (
                <ExclamationCircleOutlined />
              ) : (
                <FileExcelOutlined />
              )}
            </span>
            <span className="exportFileText">
              <span className="exportFileName" title={row.name}>
                {row.name}
              </span>
              {row.hint ? (
                <span className="exportFileHint">{row.hint}</span>
              ) : null}
            </span>
          </div>
        ),
      },
      {
        title: "Created",
        key: "when",
        width: 150,
        render: (_, row) =>
          row.rowType === "file" ? (
            <Tooltip title={formatAbsolute(row.createdDate)}>
              <span className="exportWhen">{row.when}</span>
            </Tooltip>
          ) : (
            <span className="exportWhen is-muted">{row.when}</span>
          ),
      },
      {
        title: "",
        key: "action",
        width: 96,
        align: "right",
        render: (_, row) =>
          row.rowType === "pending" ? (
            <Tag
              className={`exportTag ${
                phase === "watching" ? "is-running" : "is-paused"
              }`}
            >
              {phase === "watching" ? "Running" : "Paused"}
            </Tag>
          ) : (
            <Tooltip title={row.url ? "Download" : "Not available"}>
              <Button
                type="text"
                size="small"
                className="exportDownloadBtn"
                icon={<DownloadOutlined />}
                disabled={!row.url}
                onClick={() => downloadExportFile(row)}
                aria-label={`Download ${row.name}`}
              />
            </Tooltip>
          ),
      },
    ],
    [phase]
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={760}
      centered
      className="exportPanel"
      styles={{ body: { padding: 0 } }}
    >
      <div className="exportPanelHead">
        <span className={`exportPanelHeadIcon${isPdf ? " is-pdf" : ""}`}>
          {isPdf ? <FilePdfOutlined /> : <FileExcelOutlined />}
        </span>
        <div className="exportPanelHeadText">
          <h2>{copy.title}</h2>
          <p>{copy.subtitle}</p>
        </div>
        <Button
          type="text"
          className="exportPanelClose"
          icon={<CloseOutlined />}
          onClick={onClose}
          aria-label="Close export window"
        />
      </div>

      <div className="exportPanelBody">
        {/* Format first: it changes what the controls under it mean, so it has
            to be read before them, not alongside them. */}
        <Segmented
          className="exportFormatSwitch"
          block
          value={format}
          onChange={setFormat}
          options={FORMAT_OPTIONS}
        />

        <div className="exportNew">
          <div className="exportNewMeta">
            <span
              className={`exportScopePill${nothingSelected ? " is-empty" : ""}`}
            >
              {isSelection ? `${selectedCount} selected` : copy.pill}
            </span>
            <span className="exportScopeHint">
              {nothingSelected
                ? "Tick at least one site visit in the table to export."
                : copy.hint}
            </span>
          </div>
          <div className="exportNewActions">
            {isPdf ? (
              <Segmented
                size="small"
                className="exportColumnSetSwitch"
                value={columnSet}
                onChange={setColumnSet}
                options={COLUMN_SET_OPTIONS}
              />
            ) : (
              <Checkbox
                checked={includeMedia}
                onChange={(e) => setIncludeMedia(e.target.checked)}
              >
                Include media files
              </Checkbox>
            )}
            <Tooltip
              title={
                nothingSelected ? "You must select a site visit to export." : ""
              }
            >
              <Button
                type="primary"
                className="exportGenerateBtn"
                loading={isPdf ? buildingPdf : submitting}
                disabled={nothingSelected}
                onClick={isPdf ? handleDownloadPdf : handleGenerate}
              >
                {isPdf ? "Download PDF" : "Generate Excel"}
              </Button>
            </Tooltip>
          </div>
        </div>

        {isPdf ? (
          <p className="exportInlineNote">
            The whole table is written to the PDF, not just the page on screen —
            {columnSet === "all"
              ? " every column, on A3 landscape."
              : " the key columns, on A4 landscape."}{" "}
            It is built in your browser and saves straight to your downloads.
          </p>
        ) : includeMedia ? (
          <p className="exportInlineNote">
            Media is zipped alongside the workbook — large selections take
            noticeably longer to build.
          </p>
        ) : null}

        {pdfError ? (
          <Alert
            type="error"
            showIcon
            className="exportAlert"
            message={pdfError}
          />
        ) : null}

        {/* Deliberately not an antd Alert: colorInfo is the brand magenta in
            this app, so an info Alert shouts as loudly as an error. */}
        {pending.length && phase === "watching" ? (
          <p className="exportLiveNote">
            <LoadingOutlined /> Building your export on the server — you can
            close this window, we'll notify you with a download link when it's
            ready.
          </p>
        ) : null}

        {/* Everything below belongs to the Excel job: its alerts, and the list
            of server-side files. A PDF never lands there, so showing "Recent
            exports" under a PDF button would only invite people to wait for a
            row that is never coming. */}
        {isPdf ? null : (
          <>
            {phase === "timedOut" ? (
              <Alert
                type="warning"
                showIcon
                className="exportAlert"
                message="Still generating"
                description="This export is taking longer than usual, which is normal for large projects with media. We stopped checking automatically."
                action={
                  <Button size="small" onClick={retry}>
                    Keep checking
                  </Button>
                }
              />
            ) : null}

            {stalled.length ? (
              <Alert
                type="warning"
                showIcon
                closable
                onClose={dismissStalled}
                className="exportAlert"
                message="Export not confirmed"
                description="The server reports no export running and no new file arrived. Start the export again."
              />
            ) : null}

            {listError ? (
              <Alert
                type="error"
                showIcon
                className="exportAlert"
                message={listError}
                action={
                  <Button size="small" onClick={refresh}>
                    Retry
                  </Button>
                }
              />
            ) : null}

            <div className="exportListHead">
              <span className="exportListTitle">Recent exports</span>
              <span className="exportListMeta">
                {lastChecked ? `Checked ${relativeFrom(lastChecked)}` : null}
                <Button
                  type="text"
                  size="small"
                  className="exportRefreshBtn"
                  icon={<ReloadOutlined />}
                  loading={loadingFiles}
                  onClick={refresh}
                >
                  Refresh
                </Button>
              </span>
            </div>

            <Table
              className="exportTable"
              size="small"
              rowKey="key"
              columns={columns}
              dataSource={rows}
              loading={loadingFiles && !rows.length}
              pagination={
                rows.length > 6 ? { pageSize: 6, size: "small", showSizeChanger: false } : false
              }
              scroll={{ y: 268 }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No exports yet — generate one above."
                  />
                ),
              }}
            />
          </>
        )}
      </div>

      <div className="exportPanelFoot">
        <span className="exportFootNote">
          {isPdf
            ? "PDFs are built in your browser and save straight to your downloads — they are not kept on the server."
            : "Exports are generated on the server and stay listed here for later download."}
        </span>
        <Button className="exportCloseBtn" onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
};

export default ExportPanel;
